import React, { useEffect, useRef } from 'react';
import { Field } from 'signer-app/types/editor-types';
import { useSignerContext } from 'signer-app/signer-experience/signer-experience';
import { AccessibleTextField } from 'signer-app/signer-signature-document/field-components/accessible-text-field';
import { SignatureFieldComponent } from 'signer-app/signer-signature-document/field-components/signature-field';
import { Hyperlink } from 'signer-app/signer-signature-document/field-components/hyperlink';
import { Checkbox, RadioButton } from '@dropbox/dig-components/controls';
import { UnreachableError } from 'signer-app/utils/unreachable';
import { Select, TextInput } from '@dropbox/dig-components/text_fields';
import { signatureRequestContext } from 'signer-app/signature-request/context';

type FieldProps = {
  field: Field;
};
export function FieldInput({ field }: FieldProps) {
  const { updateFields, selectField, getInstantValidationErrorHack } =
    useSignerContext();
  const { selectedFieldIds } = React.useContext(signatureRequestContext);
  const type = field.type;
  const isActive = selectedFieldIds.includes(field.id);
  const fieldRef = useRef<any>(null);
  const handleFocus = React.useCallback(() => {
    if (!isActive) {
      selectField(field.id);
    }
  }, [field.id, isActive, selectField]);

  useEffect(() => {
    const element = fieldRef.current;

    if (!(isActive && element)) {
      return;
    }

    // The DIG dropdown does not expose a way to focus the button
    if (type === 'dropdown') {
      element.querySelector('button')?.focus();
    } else {
      element.focus();
    }
  }, [isActive, type]);

  switch (type) {
    case 'text': {
      return (
        <AccessibleTextField
          ref={fieldRef}
          onFocus={handleFocus}
          isActive={isActive}
          updateField={(update) => {
            updateFields({
              [field.id]: update,
            });
          }}
          fieldData={field}
          getInstantValidationErrorHack={getInstantValidationErrorHack}
          onValueChange={(value) => {
            updateFields({
              [field.id]: {
                value,
              },
            });
          }}
        />
      );
    }
    case 'date':
      return (
        <TextInput
          ref={fieldRef}
          placeholder={field.placeholder}
          value={field.value ?? ''}
          onFocus={handleFocus}
          data-form-view-field={field.id}
          size="large"
          onChange={(e) =>
            updateFields({
              [field.id]: {
                value: e.target.value,
                lines: e.target.value,
              },
            })
          }
          id={`field_${field.id}`}
          readOnly={true}
          aria-describedby={`validation_${field.id}`}
          data-chromatic="ignore"
        />
      );
    case 'dropdown':
      return (
        <div ref={fieldRef}>
          <Select
            id={`field_${field.id}`}
            onFocus={handleFocus}
            data-form-view-field={field.id}
            size="large"
            value={field.value ?? undefined}
            onChange={(value) =>
              updateFields({
                [field.id]: { value },
              })
            }
            aria-describedby={`validation_${field.id}`}
          >
            {field.options.map((option) => (
              <Select.Option key={option} value={option}>
                {option}
              </Select.Option>
            ))}
          </Select>
        </div>
      );
    case 'checkbox':
      /**
       * The div is necessary for the drawer, it is used for sizing.  Without
       * it, the checkbox is stretched to cover the whole area.
       */
      return (
        <div>
          <Checkbox
            ref={fieldRef}
            // This needs to be true for the focus styles to be applied
            data-focus-visible-added={isActive}
            onChange={(e) =>
              updateFields({
                [field.id]: {
                  checked: e.target.checked,
                },
              })
            }
            data-form-view-field={field.id}
            onFocus={handleFocus}
            id={`field_${field.id}`}
            checked={field.checked ?? false}
            aria-describedby={`validation_${field.id}`}
          />
        </div>
      );
    case 'radiobutton':
      return (
        <RadioButton
          ref={fieldRef}
          // This needs to be true for the focus styles to be applied
          data-focus-visible-added={isActive}
          onChange={(e) =>
            updateFields({
              [field.id]: {
                checked: e.target.checked,
              },
            })
          }
          onFocus={handleFocus}
          data-form-view-field={field.id}
          name={`radio_${field.id}`}
          checked={field.checked}
          id={`field_${field.id}`}
        />
      );
    case 'signature':
    case 'initials':
      return (
        <SignatureFieldComponent
          ref={fieldRef}
          data-form-view-field={field.id}
          field={field}
          onFocus={handleFocus}
        />
      );
    case 'hyperlink':
      return <Hyperlink field={field} />;
    case 'image':
    case 'rectangle':
      return null;
    default:
      throw new UnreachableError(type);
  }
}
